/**
 * Patient Histories Page
 *
 * 患者履歴 / 受診者履歴 — Patient History Screen (BD §13–16)
 * Shows chronological history of patient information, insurance cards, and medical aids (read-only; no 操作 column vs legacy A2).
 */

import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

import dayjs from 'dayjs';

import { PatientInformation, HealthInsuranceCard, MedicalAid } from '@pages/main/patient/types';
import { useMainPatientEdit, useMainPatientLookupData } from '@hooks/main';
import PatientNavigationButtons, {
  getTreatmentFlowContextFromPatient,
} from '@pages/main/patient/components/PatientNavigationButtons';
import { AppCard, AppTable } from '@/components/atoms';
import { NotFound } from '@/pages/main/system/NotFound';

type PatientInfoRow = PatientInformation & { date_from: string; date_to: string };

/** BD §14–16: yyyy-mm-dd; blank when missing or indefinite sentinel */
function formatHistoryDateYmd(value: string | null | undefined): string {
  if (value == null || String(value).trim() === '') return '';
  const s = String(value).trim();
  if (s === '2100-12-31' || s === '2100-31-12') return '';
  const head = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  const d = dayjs(s);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
}

/** BD §14.7: {postal1}-{postal2}{addr1}{addr2}{addr3} (no space) */
function formatPostalAddressLine(record: PatientInformation): string {
  const hasPostal = Boolean(record.postal_code1?.trim() || record.postal_code2?.trim());
  const postal = hasPostal ? `${record.postal_code1 ?? ''}-${record.postal_code2 ?? ''}` : '';
  const body = `${record.address1 ?? ''}${record.address2 ?? ''}${record.address3 ?? ''}`;
  return `${postal}${body}`;
}

/** BD §14.4 */
function formatPatientHistoryDisplayName(record: PatientInformation): string {
  const n = record.name?.trim() ?? '';
  const k = record.name_kana?.trim() ?? '';
  if (!n && !k) return '';
  if (!n) return `(${k})`;
  if (!k) return n;
  return `${n}(${k})`;
}

function compareQualificationThenExpiration(
  a: HealthInsuranceCard,
  b: HealthInsuranceCard
): number {
  const q = (c: HealthInsuranceCard) =>
    c.qualification_date ? dayjs(c.qualification_date).valueOf() : Number.NEGATIVE_INFINITY;
  const e = (c: HealthInsuranceCard) =>
    c.expiration_date ? dayjs(c.expiration_date).valueOf() : Number.NEGATIVE_INFINITY;
  const dq = q(a) - q(b);
  if (dq !== 0) return dq;
  return e(a) - e(b);
}

function compareMedicalAidSort(a: MedicalAid, b: MedicalAid): number {
  const s = (c: MedicalAid) =>
    c.application_start_date ? dayjs(c.application_start_date).valueOf() : Number.NEGATIVE_INFINITY;
  const e = (c: MedicalAid) =>
    c.application_end_date ? dayjs(c.application_end_date).valueOf() : Number.NEGATIVE_INFINITY;
  const ds = s(a) - s(b);
  if (ds !== 0) return ds;
  return e(a) - e(b);
}

function formatCostRateWari(rate: string | number | null | undefined): string {
  if (rate === null || rate === undefined || rate === '') return '';
  return `${rate}割`;
}

const PatientHistoriesPage: React.FC = () => {
  const { id, tenant } = useParams<{ id: string; tenant?: string }>();
  const navigate = useNavigate();

  const {
    data: patientData,
    isLoading,
    isError,
  } = useMainPatientEdit(id ? parseInt(id, 10) : undefined);
  const { data: lookupData } = useMainPatientLookupData();

  const masterMaps = useMemo(() => {
    const toMap = (rows: { id: number; name: string }[] | undefined) => {
      const m = new Map<number, string>();
      for (const r of rows ?? []) m.set(r.id, r.name);
      return m;
    };
    const insurerTypes = lookupData?.insurer_types ?? [];
    return {
      sex: toMap(lookupData?.sexs),
      relationship: toMap(lookupData?.relationships),
      insuranceType: toMap(lookupData?.insurance_types),
      insurerType: toMap(insurerTypes),
      noneInsurerTypeIds: new Set(
        insurerTypes.filter(t => t.name === 'なし' || t.name === '無し').map(t => t.id)
      ),
    };
  }, [lookupData]);

  const patientInfoHistoryRows = useMemo((): PatientInfoRow[] => {
    if (!patientData) return [];
    const sorted = [...patientData.patient_informations].sort((a, b) => {
      const ta = a.available_to ? dayjs(a.available_to).valueOf() : Number.POSITIVE_INFINITY;
      const tb = b.available_to ? dayjs(b.available_to).valueOf() : Number.POSITIVE_INFINITY;
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    });

    // Path A: Find oldest Insurance Card's qualification date as the fallback start date for the oldest PI record
    const sortedHics = [...patientData.health_insurance_cards].sort(
      compareQualificationThenExpiration
    );
    const oldestQualDate = sortedHics[0]?.qualification_date;

    return sorted.map((info, index) => {
      const prev = sorted[index - 1];
      let dateFrom =
        formatHistoryDateYmd(info.available_from) ||
        (prev?.available_to && String(prev.available_to).trim() !== ''
          ? dayjs(prev.available_to).add(1, 'day').format('YYYY-MM-DD')
          : '');

      const dateTo = formatHistoryDateYmd(info.available_to);
      const oldestQualYmd = formatHistoryDateYmd(oldestQualDate);

      // For the oldest patient info record of an official patient, fallback to the
      // oldest insurance card start only when it does not invert the displayed range.
      if (
        index === 0 &&
        dateFrom === '' &&
        patientData.official &&
        oldestQualYmd &&
        (!dateTo || oldestQualYmd <= dateTo)
      ) {
        dateFrom = oldestQualYmd;
      }

      return {
        ...info,
        date_from: dateFrom,
        date_to: dateTo,
      };
    });
  }, [patientData]);

  const sortedInsuranceCards = useMemo(() => {
    if (!patientData) return [];
    return [...patientData.health_insurance_cards].sort(compareQualificationThenExpiration);
  }, [patientData]);

  const sortedMedicalAids = useMemo(() => {
    if (!patientData) return [];
    return [...patientData.medical_aids].sort(compareMedicalAidSort);
  }, [patientData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  if (isError || !patientData) {
    return <NotFound />;
  }

  const patientInfoColumns: ColumnsType<PatientInfoRow> = [
    {
      title: '有効期限FROM',
      dataIndex: 'date_from',
      key: 'date_from',
      width: 120,
      align: 'left',
    },
    {
      title: '有効期限TO',
      dataIndex: 'date_to',
      key: 'date_to',
      width: 120,
      align: 'left',
    },
    {
      title: '氏名(カナ)',
      key: 'name',
      align: 'left',
      render: (_: unknown, record) => formatPatientHistoryDisplayName(record),
      width: 200,
    },
    {
      title: '性別',
      dataIndex: 'sex_id',
      key: 'sex_id',
      align: 'left',
      render: (sexId: number | null) => {
        if (sexId == null) return '';
        const name = masterMaps.sex.get(sexId) ?? '';
        if (name === '男性') return '男';
        if (name === '女性') return '女';
        return name;
      },
      width: 80,
    },
    {
      title: '生年月日',
      dataIndex: 'date_of_birth',
      key: 'date_of_birth',
      align: 'left',
      render: (dob: string | null | undefined) => formatHistoryDateYmd(dob),
      width: 120,
    },
    {
      title: '住所',
      key: 'address',
      align: 'left',
      render: (_: unknown, record) => formatPostalAddressLine(record),
      width: 300,
    },
    {
      title: '電話番号',
      dataIndex: 'telephone',
      key: 'telephone',
      align: 'left',
      width: 150,
    },
  ];

  const insuranceColumns: ColumnsType<HealthInsuranceCard> = [
    {
      title: '資格取得日',
      dataIndex: 'qualification_date',
      key: 'qualification_date',
      width: 120,
      align: 'left',
      render: (v: string | null | undefined) => formatHistoryDateYmd(v),
    },
    {
      title: '有効期限',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 120,
      align: 'left',
      render: (v: string | null | undefined) => formatHistoryDateYmd(v),
    },
    {
      title: '種別',
      dataIndex: 'insurance_type_id',
      key: 'insurance_type_id',
      width: 100,
      align: 'left',
      render: (typeId: number | null) =>
        typeId != null ? (masterMaps.insuranceType.get(typeId) ?? '') : '',
    },
    {
      title: '保険者番号',
      dataIndex: 'insurer_number',
      key: 'insurer_number',
      width: 120,
      align: 'left',
    },
    {
      title: '保険者名',
      dataIndex: 'insurer_name',
      key: 'insurer_name',
      width: 150,
      align: 'left',
    },
    {
      title: '負担割合',
      dataIndex: 'patient_cost_rate',
      key: 'patient_cost_rate',
      width: 100,
      align: 'left',
      render: (rate: string | number | null | undefined) => formatCostRateWari(rate),
    },
    {
      title: '記号・番号',
      key: 'card',
      width: 120,
      align: 'left',
      render: (_: unknown, record) => `${record.card_mark ?? ''}${record.card_number ?? ''}`,
    },
    {
      title: '続柄',
      dataIndex: 'relationship_id',
      key: 'relationship_id',
      width: 80,
      align: 'left',
      render: (relId: number | null) =>
        relId != null ? (masterMaps.relationship.get(relId) ?? '') : '',
    },
  ];

  const medicalAidColumns: ColumnsType<MedicalAid> = [
    {
      title: '適用開始日',
      dataIndex: 'application_start_date',
      key: 'application_start_date',
      width: 120,
      align: 'left',
      render: (v: string | null | undefined) => formatHistoryDateYmd(v),
    },
    {
      title: '適用終了日',
      dataIndex: 'application_end_date',
      key: 'application_end_date',
      width: 120,
      align: 'left',
      render: (v: string | null | undefined) => formatHistoryDateYmd(v),
    },
    {
      title: '種別',
      dataIndex: 'insurer_type_id',
      key: 'insurer_type_id',
      width: 120,
      align: 'left',
      // Match MedicalAidSection / A2: master data may use 「なし」 but UI label is 「無し」
      render: (typeId: number | null) => {
        if (typeId == null) return '';
        if (masterMaps.noneInsurerTypeIds.has(typeId)) return '無し';
        return masterMaps.insurerType.get(typeId) ?? '';
      },
    },
    {
      title: '公費負担者番号',
      dataIndex: 'medical_aid_number',
      key: 'medical_aid_number',
      width: 150,
      align: 'left',
    },
    {
      title: '保険者名',
      key: 'insurer_display_name',
      width: 150,
      align: 'left',
      render: (_: unknown, record) => {
        if (
          record.insurer_type_id != null &&
          masterMaps.noneInsurerTypeIds.has(record.insurer_type_id)
        ) {
          return '';
        }
        return record.insurer_name ?? '';
      },
    },
    {
      title: '負担割合',
      dataIndex: 'insurer_cost_rate',
      key: 'insurer_cost_rate',
      width: 100,
      align: 'left',
      render: (rate: string | number | null | undefined) => formatCostRateWari(rate),
    },
    {
      title: '公費受給者番号',
      dataIndex: 'medical_aid_patient_number',
      key: 'medical_aid_patient_number',
      width: 150,
      align: 'left',
    },
  ];

  return (
    <div>
      <div className="mb-6 flex justify-between">
        <h2 className="m-0 text-xl font-semibold">受診者履歴 </h2>
        <PatientNavigationButtons
          id={id!}
          tenant={tenant}
          navigate={navigate}
          treatmentFlowContext={getTreatmentFlowContextFromPatient(patientData)}
          pdfTemplateMenu="full"
        />
      </div>

      <AppCard title="住所情報履歴" className="mb-6">
        <AppTable
          columns={patientInfoColumns}
          dataSource={patientInfoHistoryRows}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </AppCard>

      <AppCard title="保険証情報履歴" className="mb-6">
        <AppTable
          columns={insuranceColumns}
          dataSource={sortedInsuranceCards}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </AppCard>

      <AppCard title="医療助成情報履歴" className="mb-6">
        <AppTable
          columns={medicalAidColumns}
          dataSource={sortedMedicalAids}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </AppCard>
    </div>
  );
};

export default PatientHistoriesPage;
